import { Component, EventEmitter, OnInit, Output, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable, of, Subscription } from 'rxjs';
import { map, startWith, filter, switchMap } from 'rxjs/operators';
import { iParsedEntity, iParsedEntityWithDisplay } from '@app/Shared/interfaces/iEntityParsed';
import { filterEntities } from '@app/Shared/util/filterEntities';
import { Store, select } from '@ngrx/store';
import { AppState } from '@app/Store/reducers';
import { dashboardFeatureSelectors } from '@app/Store/features/dashboard.feature';
import * as DashboardActions from '@app/Store/actions/dashboard-actions';
import { selectToken } from '@app/Store/features/auth.feature';
import { META_DATA, MASTER_TABLES } from '@app/Shared/constants/td-constants';

@Component({
    selector: 'app-entity-selector',
    templateUrl: './entity-selector.component.html',
    styleUrls: ['./entity-selector.component.scss'],
})
export class EntitySelectorComponent implements OnInit, OnDestroy {
    @Output() entitySelected = new EventEmitter<iParsedEntity>();

    public entityControl = new FormControl();
    public filteredOptions$: Observable<iParsedEntityWithDisplay[]> = of([]);
    public options: iParsedEntity[] = [];
    public masterTables: any[] = [];
    public showDropdown = false;
    public selectedEntity: iParsedEntity | null = null;

    private subscription = new Subscription();

    constructor(private store: Store<AppState>) {}

    ngOnInit(): void {
        this.loadData();
        this.initializeEntitySelector();
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    private loadData(): void {
        // Load entities and master tables when component initializes
        this.subscription.add(
            this.store
                .pipe(
                    select(selectToken),
                    filter((token) => !!token),
                    switchMap(() => {
                        // Dispatch actions to load entities and master tables
                        this.store.dispatch(DashboardActions.loadDataEntities({ query: META_DATA }));
                        this.store.dispatch(DashboardActions.loadDataMasterTables({ query: MASTER_TABLES }));
                        return of(null);
                    }),
                )
                .subscribe(),
        );
    }
    private initializeEntitySelector(): void {
        // Subscribe to entities from store
        this.subscription.add(
            this.store.pipe(select(dashboardFeatureSelectors.selectEntities)).subscribe((entities) => {
                this.options = entities;
                this.setupEntityFiltering();
            }),
        );

        // Subscribe to master tables from store
        this.subscription.add(
            this.store.pipe(select(dashboardFeatureSelectors.selectMasterTables)).subscribe((masterTables) => {
                this.masterTables = masterTables;
                // Re-run filtering once masters arrive so the intersection is not empty/stale
                if (this.options.length > 0) {
                    this.setupEntityFiltering();
                }
            }),
        );

        // Subscribe to selected entity from store
        this.subscription.add(
            this.store.pipe(select(dashboardFeatureSelectors.selectSelectedEntity)).subscribe((selectedEntity) => {
                this.selectedEntity = selectedEntity;
                if (selectedEntity) {
                    this.entityControl.setValue(this.resolveDisplayName(selectedEntity));
                }
            }),
        );
    }

    private setupEntityFiltering(): void {
        this.filteredOptions$ = this.entityControl.valueChanges.pipe(
            startWith(''),
            map((value: string) => this.filterPrecomputedOptions(value)),
        );
    }

    private resolveDisplayName(entity: iParsedEntity): string {
        const matched = filterEntities('', [entity], this.masterTables)[0];
        return matched?.displayName || entity.entityName;
    }
    private filterPrecomputedOptions(value: string): iParsedEntityWithDisplay[] {
        // When the input shows a selected display name, keep the full list available for browsing
        if (this.selectedEntity && value === this.resolveDisplayName(this.selectedEntity)) {
            return filterEntities('', this.options, this.masterTables);
        }
        return filterEntities(value || '', this.options, this.masterTables);
    }

    public onEntityFocus(): void {
        this.showDropdown = true;
    }

    public onEntityBlur(): void {
        // Delay hiding dropdown to allow for clicks
        setTimeout(() => {
            this.showDropdown = false;
        }, 200);
    }

    public onEntityOptionSelected(entity: iParsedEntityWithDisplay | iParsedEntity, event?: Event): void {
        // Prevent bubble to wrapper (click)="onEntityFocus()" which would reopen the dropdown
        event?.stopPropagation();

        const displayName = 'displayName' in entity && entity.displayName ? entity.displayName : entity.entityName;
        this.entityControl.setValue(displayName);
        this.showDropdown = false;

        const selectedEntity: iParsedEntity = {
            entityName: entity.entityName,
            key: entity.key,
            properties: entity.properties,
            navigationProperties: entity.navigationProperties,
            rowData: null,
            totalCount: null,
            isSingleRecord: false,
            selectedRecords: null,
        };
        this.store.dispatch(DashboardActions.selectedEntity({ selectedEntity }));
        this.entitySelected.emit(selectedEntity);
    }

    public clearEntityField(event?: Event): void {
        if (event) {
            event.stopPropagation();
        }
        this.store.dispatch(DashboardActions.clearTrueDataTable());
        this.entityControl.setValue('');
        this.showDropdown = false;
    }
}
